// Blog data cache utility
interface CacheEntry<T> {
  data: T
  timestamp: number
  expiresIn: number // milliseconds
}

const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes
const STORAGE_KEY = 'blog_cache'

// Memory cache
const memoryCache = new Map<string, CacheEntry<any>>()

// Get from cache
export function getCachedData<T>(key: string): T | null {
  // Check memory cache first
  const memoryEntry = memoryCache.get(key)
  if (memoryEntry && Date.now() - memoryEntry.timestamp < memoryEntry.expiresIn) {
    return memoryEntry.data as T
  }

  // Check localStorage
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY}_${key}`)
    if (stored) {
      const entry: CacheEntry<T> = JSON.parse(stored)
      if (Date.now() - entry.timestamp < entry.expiresIn) {
        // Also store in memory cache
        memoryCache.set(key, entry)
        return entry.data
      } else {
        // Expired, remove it
        localStorage.removeItem(`${STORAGE_KEY}_${key}`)
      }
    }
  } catch (e) {
    // localStorage might be disabled or full
    console.warn('Failed to read from localStorage cache:', e)
  }

  return null
}

// Set cache
export function setCachedData<T>(key: string, data: T, expiresIn: number = CACHE_DURATION): void {
  const entry: CacheEntry<T> = {
    data,
    timestamp: Date.now(),
    expiresIn,
  }

  // Store in memory cache
  memoryCache.set(key, entry)

  // Store in localStorage
  try {
    localStorage.setItem(`${STORAGE_KEY}_${key}`, JSON.stringify(entry))
  } catch (e) {
    // localStorage might be full, that's okay
    console.warn('Failed to write to localStorage cache:', e)
  }
}

// Clear cache
export function clearCache(key?: string): void {
  if (key) {
    memoryCache.delete(key)
    try {
      localStorage.removeItem(`${STORAGE_KEY}_${key}`)
    } catch (e) {
      console.warn('Failed to clear localStorage cache:', e)
    }
  } else {
    memoryCache.clear()
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith(STORAGE_KEY))
        .forEach((k) => localStorage.removeItem(k))
    } catch (e) {
      console.warn('Failed to clear localStorage cache:', e)
    }
  }
}

